import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const EditStaff = () => {
  const { staffId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    if (staffId) {
      fetchStaffData();
    }
  }, [staffId, isAuthenticated, user, navigate]);

  const fetchStaffData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/staff/${staffId}`);
      setStaffData(response.data);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      if (error.response?.status === 404) {
        setError('Staff member not found');
      } else {
        setError('Error retrieving staff information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditSection = (section) => {
    switch (section) {
      case 'staffinfo':
        navigate(`/edit-staff/${staffId}/info`);
        break;
      case 'job':
        navigate(`/edit-staff/${staffId}/job`);
        break;
      case 'previouswork':
        navigate(`/edit-staff/${staffId}/work-experience`);
        break;
      case 'training':
        navigate(`/edit-staff/${staffId}/training`);
        break;
      case 'benefits':
        navigate(`/edit-staff/${staffId}/benefits`);
        break;
      case 'capacity':
        navigate(`/edit-staff/${staffId}/skills`);
        break;
      default:
        break;
    }
  };

  const handleBackToList = () => {
    navigate('/staff-management');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h4 className="text-xl font-semibold text-gray-800 mb-2">Loading Staff Data...</h4>
            <p className="text-gray-600">Please wait while we fetch the staff information.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !staffData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-red-500 mb-4" style={{ fontSize: '3rem' }}>❌</div>
            <h4 className="text-xl font-semibold text-red-600 mb-4">Error Loading Staff Data</h4>
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <strong>Error:</strong> {error}
            </div>
            <div className="flex gap-4 justify-center">
              <button 
                onClick={fetchStaffData}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Retry Loading
              </button>
              <button
                onClick={handleBackToList}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
              >
                Back to Staff List
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!staffData) {
    return null;
  }

  const sections = [
    {
      id: 'staffinfo',
      title: 'Staff Information',
      description: 'Personal details, contact information, and identification',
     
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'job',
      title: 'Job Information',
      description: 'Current position, work location, and employment details',
      
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'previouswork',
      title: 'Previous Work Experience',
      description: 'Employment history and work experience',
     
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'training',
      title: 'Training Records',
      description: 'Training courses, certifications, and scores',
      
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'benefits',
      title: 'Benefits & Compensation',
      description: 'Salary, benefits, and compensation details',
      
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'capacity',
      title: 'Skills & Capacity',
      description: 'Language skills and computer skills',
      
      color: 'bg-blue-500 hover:bg-blue-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-7xl mx-auto p-5 pt-20">
        {/* Header */}
        <div className="text-center mb-8 p-6 bg-white rounded-xl shadow-lg">
          <h1 className="text-3xl font-bold text-gray-800 mb-3">Edit Staff Information</h1>
          <p className="text-gray-600 text-lg mb-4">
            Editing details for: <strong>{staffData.fullname}</strong> (ID: {staffData.staff_id})
          </p>
          <button
            onClick={handleBackToList}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-semibold transition-colors"
          >
            ← Back to Staff List
          </button>
        </div>

        {/* Error Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Section Selector */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Choose Section to Edit</h2>
          <p className="text-gray-600 text-center mb-8">
            Select which section you want to edit for <strong>{staffData.fullname}</strong>
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sections.map((section) => (
              <div
                key={section.id}
                onClick={() => handleEditSection(section.id)}
                className={`${section.color} text-white p-6 rounded-xl shadow-lg cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl`}
              >
                <div className="text-center">
                  <div className="text-4xl mb-3">{section.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{section.title}</h3>
                  <p className="text-sm opacity-90">{section.description}</p>
                  <div className="mt-4 flex justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Staff Overview */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
              <div>
                <h2 className="text-2xl font-bold mb-2">{staffData.fullname}</h2>
                <div className="flex items-center gap-4">
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                    ID: {staffData.staff_id}
                  </span>
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                    Created: {staffData.created_at ? new Date(staffData.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="mt-4 sm:mt-0">
                <span className="bg-green-400 text-green-900 px-3 py-1 rounded-full text-sm font-medium">
                  Active Record
                </span>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">📧 Contact Information</h4>
                <p className="text-sm text-gray-600">Email: {staffData.email || 'Not provided'}</p>
                <p className="text-sm text-gray-600">Phone: {staffData.phone_number || 'Not provided'}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">💼 Current Position</h4>
                <p className="text-sm text-gray-600">Position: {staffData.current_workposition || 'Not specified'}</p>
                <p className="text-sm text-gray-600">Clinic: {staffData.clinic_name || 'Not specified'}</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">👤 Personal Details</h4>
                <p className="text-sm text-gray-600">Gender: {staffData.gender_name || 'Not specified'}</p>
                <p className="text-sm text-gray-600">DOB: {staffData.date_of_birth ? new Date(staffData.date_of_birth).toLocaleDateString() : 'Not provided'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStaff;
